import React, { useState } from 'react';
import {
  Shield,
  Scale,
  FileText,
  CheckCircle2,
  Lock,
  Globe,
  Eye,
  Sliders,
  HelpCircle,
  ExternalLink,
  BookOpen,
  Receipt,
  RotateCcw,
  Clock,
  UserCheck,
  Send,
  AlertCircle
} from 'lucide-react';
import { AppView } from '../../types';
import {
  LEGAL_DOCUMENTS,
  CONTEXTUAL_DISCLOSURES,
  TRACKING_TECHNOLOGIES_REGISTRY
} from '../../services/compliance/complianceMockData';
import { complianceEngine } from '../../services/compliance/complianceEngine';
import { PrivacyRightType } from '../../services/compliance/complianceTypes';

type PortalTab =
  | 'terms'
  | 'privacy'
  | 'cookies'
  | 'refunds'
  | 'ai-disclosure'
  | 'accessibility'
  | 'disclosures'
  | 'dsar-request';

interface Props {
  onNavigateToView: (v: AppView) => void;
}

export const LegalAndCompliancePortal: React.FC<Props> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<PortalTab>('terms');

  // DSAR Form State
  const [dsarEmail, setDsarEmail] = useState('');
  const [dsarRightType, setDsarRightType] = useState<PrivacyRightType>('ACCESS');
  const [dsarJurisdiction, setDsarJurisdiction] = useState('European Union (GDPR)');
  const [dsarSubmittedResult, setDsarSubmittedResult] = useState<any>(null);

  // Cookie CMP Preferences state
  const [cookiePrefs, setCookiePrefs] = useState({
    essential: true, // immutable
    functional: true,
    analytics: false,
    advertising: false
  });
  const [cookieSavedNotice, setCookieSavedNotice] = useState(false);

  // Accessibility Feedback state
  const [a11yFeedbackText, setA11yFeedbackText] = useState('');
  const [a11yFeedbackSent, setA11yFeedbackSent] = useState(false);

  const handleDSARSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dsarEmail) return;
    const req = complianceEngine.submitDSAR(dsarEmail, dsarRightType, dsarJurisdiction);
    setDsarSubmittedResult(req);
  };

  const handleSaveCookiePrefs = () => {
    setCookieSavedNotice(true);
    setTimeout(() => setCookieSavedNotice(false), 4000);
  };

  const handleA11ySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!a11yFeedbackText) return;
    setA11yFeedbackSent(true);
    setTimeout(() => {
      setA11yFeedbackSent(false);
      setA11yFeedbackText('');
    }, 5000);
  };

  const currentDoc = LEGAL_DOCUMENTS.find((d) => {
    if (activeTab === 'terms') return d.slug === 'terms-of-service';
    if (activeTab === 'privacy') return d.slug === 'privacy-policy';
    if (activeTab === 'cookies') return d.slug === 'cookie-policy';
    if (activeTab === 'refunds') return d.slug === 'refund-cancellation-policy';
    if (activeTab === 'ai-disclosure') return d.slug === 'ai-disclosure';
    if (activeTab === 'accessibility') return d.slug === 'accessibility-statement';
    return false;
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24 text-[#0F172A]">
      {/* Header Banner */}
      <div className="bg-[#0F172A] text-white pt-16 pb-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[30rem] h-[30rem] bg-[#1E3A8A] rounded-full blur-[140px] opacity-25 pointer-events-none" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex items-center gap-3 text-[#94A3B8] mb-4 text-sm font-medium">
            <Scale className="w-4 h-4 text-[#38BDF8]" />
            <span>YamooPay Legal Center</span>
            <span>/</span>
            <span className="text-white font-bold">Consumer Rights, Privacy & Regulatory Disclosures</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-3">
                Legal & Consumer Protection Portal
              </h1>
              <p className="text-[#CBD5E1] text-base md:text-lg max-w-3xl leading-relaxed">
                Clear terms, transparent pricing, verified data privacy rights, and responsible financial intelligence disclosures.
              </p>
            </div>

            <button
              onClick={() => onNavigateToView('compliance-command-center')}
              className="px-4 py-2.5 bg-blue-900/60 hover:bg-blue-900 border border-blue-400/40 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer self-start md:self-auto"
            >
              <Shield className="w-4 h-4 text-[#38BDF8]" />
              <span>CCO Command Center</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-6xl mx-auto px-6 -mt-12 relative z-20">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 mb-8">
          {/* Navigation Bar */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E2E8F0] pb-4">
            {[
              { id: 'terms', label: 'Terms of Service', icon: FileText },
              { id: 'privacy', label: 'Privacy Policy', icon: Shield },
              { id: 'cookies', label: 'Cookie & Tracking Policy', icon: Eye },
              { id: 'refunds', label: 'Refunds & Cancellations', icon: RotateCcw },
              { id: 'ai-disclosure', label: 'AI Governance', icon: HelpCircle },
              { id: 'accessibility', label: 'Accessibility', icon: CheckCircle2 },
              { id: 'disclosures', label: 'Financial Disclosures', icon: Scale },
              { id: 'dsar-request', label: 'Submit Privacy Request (DSAR)', icon: UserCheck }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as PortalTab)}
                  className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#1E40AF] text-white shadow-xs'
                      : 'bg-white text-[#475569] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* DOCUMENT VIEWER (Terms, Privacy, Cookies, Refunds, AI, Accessibility) */}
          {currentDoc && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-[#E2E8F0] gap-2">
                <div>
                  <h2 className="text-2xl font-black text-[#0F172A]">{currentDoc.title}</h2>
                  <div className="text-xs text-[#64748B] mt-1">
                    Version <span className="font-mono font-bold text-[#1E40AF]">{currentDoc.version}</span> • Effective Date: {new Date(currentDoc.effectiveFrom).toLocaleDateString()}
                  </div>
                </div>

                <div className="px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold rounded-lg flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>Legally Certified & Active</span>
                </div>
              </div>

              {/* Markdown-style content view */}
              <div className="prose prose-slate max-w-none text-sm text-[#334155] leading-relaxed space-y-4 whitespace-pre-line font-sans">
                {currentDoc.contentMarkdown}
              </div>

              {/* Extra Interactive Cookie Preference Manager if on Cookie tab */}
              {activeTab === 'cookies' && (
                <div className="mt-8 p-6 bg-[#F8FAFC] border border-[#CBD5E1] rounded-2xl space-y-4">
                  <h3 className="text-base font-black text-[#0F172A] flex items-center gap-2">
                    <Sliders className="w-5 h-5 text-[#1E40AF]" />
                    <span>Granular Cookie Consent Manager</span>
                  </h3>
                  <p className="text-xs text-[#64748B]">
                    Manage your tracking technology preferences. Essential security cookies remain active to provide core site operations.
                  </p>

                  <div className="space-y-3 pt-2">
                    <div className="flex justify-between items-center p-3 bg-white border border-[#E2E8F0] rounded-xl text-xs">
                      <div>
                        <strong className="block text-[#0F172A]">Essential & Security Tokens</strong>
                        <span className="text-[#64748B]">Required for login sessions, CSRF safety, and fraud prevention.</span>
                      </div>
                      <span className="px-2 py-1 bg-slate-200 text-slate-700 font-bold rounded text-[11px]">ALWAYS ACTIVE</span>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-white border border-[#E2E8F0] rounded-xl text-xs">
                      <div>
                        <strong className="block text-[#0F172A]">Functional Offline Storage</strong>
                        <span className="text-[#64748B]">Caches exchange rate formulas for offline calculation capability.</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={cookiePrefs.functional}
                        onChange={(e) => setCookiePrefs({ ...cookiePrefs, functional: e.target.checked })}
                        className="w-4 h-4 text-[#1E40AF] rounded focus:ring-0"
                      />
                    </div>

                    <div className="flex justify-between items-center p-3 bg-white border border-[#E2E8F0] rounded-xl text-xs">
                      <div>
                        <strong className="block text-[#0F172A]">First-Party Analytics</strong>
                        <span className="text-[#64748B]">Helps us measure calculation error rates with pseudonymized logs.</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={cookiePrefs.analytics}
                        onChange={(e) => setCookiePrefs({ ...cookiePrefs, analytics: e.target.checked })}
                        className="w-4 h-4 text-[#1E40AF] rounded focus:ring-0"
                      />
                    </div>

                    <div className="flex justify-between items-center p-3 bg-white border border-[#E2E8F0] rounded-xl text-xs">
                      <div>
                        <strong className="block text-[#0F172A]">Contextual Advertising</strong>
                        <span className="text-[#64748B]">Permits non-personalized advertising partners in designated ad slots.</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={cookiePrefs.advertising}
                        onChange={(e) => setCookiePrefs({ ...cookiePrefs, advertising: e.target.checked })}
                        className="w-4 h-4 text-[#1E40AF] rounded focus:ring-0"
                      />
                    </div>
                  </div>

                  <div className="pt-2 flex items-center justify-between">
                    <button
                      onClick={handleSaveCookiePrefs}
                      className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-xs font-bold rounded-xl transition-colors cursor-pointer"
                    >
                      Save Consent Preferences
                    </button>

                    {cookieSavedNotice && (
                      <span className="text-xs text-emerald-600 font-bold flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4" /> Preferences saved and synchronized.
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Extra Interactive Accessibility Feedback Form if on Accessibility tab */}
              {activeTab === 'accessibility' && (
                <div className="mt-8 p-6 bg-[#F8FAFC] border border-[#CBD5E1] rounded-2xl space-y-4">
                  <h3 className="text-base font-black text-[#0F172A] flex items-center gap-2">
                    <Send className="w-5 h-5 text-[#1E40AF]" />
                    <span>Report an Accessibility Barrier</span>
                  </h3>
                  <p className="text-xs text-[#64748B]">
                    Our Accessibility Team acknowledges reports within 2 business days under European Accessibility Act (EAA) compliance guidelines.
                  </p>

                  <form onSubmit={handleA11ySubmit} className="space-y-3">
                    <textarea
                      rows={3}
                      placeholder="Describe the barrier encountered (e.g. screen reader focus trap on Loan Slider)..."
                      value={a11yFeedbackText}
                      onChange={(e) => setA11yFeedbackText(e.target.value)}
                      className="w-full p-3 bg-white border border-[#CBD5E1] rounded-xl text-xs focus:ring-2 focus:ring-[#1E40AF] focus:outline-hidden"
                    />

                    <div className="flex items-center justify-between">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-xs font-bold rounded-xl transition-colors cursor-pointer"
                      >
                        Submit Accessibility Report
                      </button>

                      {a11yFeedbackSent && (
                        <span className="text-xs text-emerald-600 font-bold flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4" /> Report logged. Case Ref #A11Y-7719 created.
                        </span>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}

          {/* TAB: FINANCIAL DISCLOSURES */}
          {activeTab === 'disclosures' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-black text-[#0F172A]">Contextual Financial Disclosures</h2>
                <p className="text-sm text-[#64748B]">
                  Regulatory statements ensuring users understand our calculation estimates and non-advisory boundaries.
                </p>
              </div>

              <div className="space-y-4">
                {CONTEXTUAL_DISCLOSURES.map((disc) => (
                  <div key={disc.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-2">
                    <div className="flex justify-between items-center">
                      <h4 className="font-bold text-base text-[#0F172A]">{disc.title}</h4>
                      <span className="font-mono text-xs text-[#1E40AF]">{disc.code}</span>
                    </div>
                    <div className="p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] text-xs text-[#334155] leading-relaxed italic">
                      "{disc.textPlainEn}"
                    </div>
                    <div className="text-xs text-[#64748B] pt-1">
                      <strong>Standard Placement:</strong> {disc.placementSurface}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB: SUBMIT PRIVACY REQUEST (DSAR) */}
          {activeTab === 'dsar-request' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-black text-[#0F172A]">Exercise Your Data Privacy Rights (DSAR)</h2>
                <p className="text-sm text-[#64748B]">
                  Submit an official request under GDPR, UK GDPR, California CCPA/CPRA, or global data protection regulations.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <form onSubmit={handleDSARSubmit} className="space-y-4 p-6 bg-[#F8FAFC] border border-[#CBD5E1] rounded-2xl">
                  <div>
                    <label className="block text-xs font-bold text-[#0F172A] mb-1">Your Account Email</label>
                    <input
                      type="email"
                      required
                      placeholder="e.g. user@example.com"
                      value={dsarEmail}
                      onChange={(e) => setDsarEmail(e.target.value)}
                      className="w-full p-3 bg-white border border-[#CBD5E1] rounded-xl text-xs focus:ring-2 focus:ring-[#1E40AF] focus:outline-hidden"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#0F172A] mb-1">Privacy Right to Exercise</label>
                    <select
                      value={dsarRightType}
                      onChange={(e) => setDsarRightType(e.target.value as PrivacyRightType)}
                      className="w-full p-3 bg-white border border-[#CBD5E1] rounded-xl text-xs focus:ring-2 focus:ring-[#1E40AF] focus:outline-hidden"
                    >
                      <option value="ACCESS">Request Copy of Personal Data (Access / Art. 15)</option>
                      <option value="DELETE">Delete My Account & Personal Data (Erasure / Art. 17)</option>
                      <option value="CORRECT">Correct Inaccurate Personal Data (Rectification / Art. 16)</option>
                      <option value="PORTABILITY">Machine-Readable Data Export (Portability / Art. 20)</option>
                      <option value="OPT_OUT_SALE_SHARE">Opt Out of Sale / Sharing (California CCPA/CPRA)</option>
                      <option value="LIMIT_SENSITIVE_USE">Limit Use of Sensitive Data (CPRA)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#0F172A] mb-1">Applicable Jurisdiction</label>
                    <select
                      value={dsarJurisdiction}
                      onChange={(e) => setDsarJurisdiction(e.target.value)}
                      className="w-full p-3 bg-white border border-[#CBD5E1] rounded-xl text-xs focus:ring-2 focus:ring-[#1E40AF] focus:outline-hidden"
                    >
                      <option value="European Union (GDPR)">European Union (GDPR)</option>
                      <option value="United Kingdom (UK GDPR)">United Kingdom (UK GDPR)</option>
                      <option value="United States (California CCPA/CPRA)">United States (California CCPA/CPRA)</option>
                      <option value="Canada (PIPEDA)">Canada (PIPEDA)</option>
                      <option value="West Africa (UEMOA)">West Africa (UEMOA)</option>
                      <option value="Global / Rest of World">Global / Rest of World</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-xs font-bold rounded-xl transition-colors cursor-pointer"
                  >
                    Submit Privacy Rights Request
                  </button>
                </form>

                <div className="space-y-4">
                  {dsarSubmittedResult ? (
                    <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-3">
                      <div className="flex items-center gap-2 text-emerald-800 font-bold text-base">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        <span>Request Received & Logged</span>
                      </div>
                      <p className="text-xs text-emerald-900 leading-relaxed">
                        Your request has been assigned tracking code <strong className="font-mono">{dsarSubmittedResult.trackingCode}</strong>.
                      </p>
                      <div className="text-xs text-emerald-950 space-y-1 pt-2 border-t border-emerald-200">
                        <div><strong>Target Email:</strong> {dsarSubmittedResult.requesterEmail}</div>
                        <div><strong>Right:</strong> {dsarSubmittedResult.rightType}</div>
                        <div><strong>Statutory Deadline:</strong> {new Date(dsarSubmittedResult.statutoryDeadline).toLocaleDateString()}</div>
                        <div><strong>Status:</strong> {dsarSubmittedResult.status}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl space-y-3 text-xs text-[#475569]">
                      <h4 className="font-bold text-sm text-[#0F172A]">How Requests Are Processed</h4>
                      <p>1. <strong>Verification:</strong> We verify your email address to prevent unauthorized account access.</p>
                      <p>2. <strong>Automated Orchestration:</strong> Requests trigger deletion across user scenarios, device tokens, and analytics IDs.</p>
                      <p>3. <strong>Statutory Retentions:</strong> Mandatory tax invoices are retained in immutable ledgers as required by law.</p>
                      <p>4. <strong>30-Day SLA:</strong> You will receive a secure confirmation link well before the statutory deadline.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
